<template>
  <div class="asd20-event-filters">
    <asd20-viewport 
      id="categories" 
      scrollable>
      <asd20-accordion
        v-for="option in sortedCalendarOptions"
        :key="
          `${option.organization.id}-${
            calendarOptionsByOrganizationAndCategory.length
          }`
        "
        :label="
          `${option.organization.abbreviation.replace(
            'ASD20',
            'District Wide'
          )}`
        "
        :description="
          option.organization.title === option.organization.abbreviation
            ? ''
            : option.organization.title
        "
        :color="option.organization.primaryColor"
        :badge="option.totalEvents || -1"
        :opened="calendarOptionsByOrganizationAndCategory.length === 1"
        expanded
        tag="h3"
      >
        <asd20-button
          v-if="option.organization.calendarLink"
          :label="option.organization.calendarLink.title"
          :link="option.organization.calendarLink.url"
          class="school-calendar-button"
          transparent
          text-only
          icon="website"
          size="sm"
        />

        <asd20-accordion
          v-for="(category, catIndex) in option.categorizedCalendarCategories"
          :key="catIndex"
          :label="category.text"
          :description="category.description"
          class="categorized-calendars"
          expanded
          tag="h4"
        >
          <asd20-event-checkbox-list
            :checked-values="selectedCalendarIds"
            :options="category.calendarOptions"
            class="nested-checkbox"
            @input="onCalendarOptionsInput"
          />
        </asd20-accordion>

        <asd20-event-checkbox-list
          :options="option.uncategorizedCalendarOptions"
          :checked-values="selectedCalendarIds"
          @input="onCalendarOptionsInput"
        />
      </asd20-accordion>
    </asd20-viewport>
  </div>
</template>

<script>
import { get, sync } from 'vuex-pathify'
import Asd20Spinner from '@asd20/ui/src/components/atoms/Asd20Spinner'
import Asd20Accordion from '@asd20/ui/src/components/molecules/Asd20Accordion'
import Asd20CheckboxList from '@asd20/ui/src/components/molecules/Asd20CheckboxList'
import Asd20EventCheckboxList from '../molecules/Asd20EventCheckboxList'
import Asd20Checkbox from '@asd20/ui/src/components/atoms/Asd20Checkbox'
import Asd20Viewport from '@asd20/ui/src/components/atoms/Asd20Viewport'
import Asd20MultiselectInput from '@asd20/ui/src/components/atoms/Asd20MultiselectInput'

export default {
  name: 'Asd20EventFilters',
  components: {
    Asd20Spinner,
    Asd20Accordion,
    Asd20CheckboxList,
    Asd20EventCheckboxList,
    Asd20Checkbox,
    Asd20Viewport,
    Asd20MultiselectInput,
  },
  data() {
    return {
      openedAccordions: {},
    }
  },
  computed: {
    totalFiltersApplied: get('totalFiltersApplied'),
    facets: get('facets'),
    calendarLocations: get('calendarLocations'),
    selectedOrganizationIds: sync('selectedOrganizationIds'),
    selectedCategoriesByOrganizationId: sync(
      'selectedCategoriesByOrganizationId'
    ),
    categoriesByOrganizationId: get('categoriesByOrganizationId'),
    calendarOptionsByOrganizationAndCategory: get(
      'calendarOptionsByOrganizationAndCategory'
    ),
    calendars: get('calendars'),
    selectedCalendarIds: sync('selectedCalendarIds'),
    sortedCalendarOptions() {
      const districtId = '26eaf390-d8ab-11e9-a3a8-5de5bba4f125'
      const allOptions = this.calendarOptionsByOrganizationAndCategory || []

      return [...allOptions].sort((a, b) => {
        if (a.organization.id === districtId) return -1
        if (b.organization.id === districtId) return 1
        return 0
      })
    },
  },
  created() {
    this.initializeSelectedCalendarIds()
  },
  methods: {
    initializeSelectedCalendarIds() {
      // If selectedCalendarIds is already set (from URL or user input), don't override it
      if (this.selectedCalendarIds && this.selectedCalendarIds.length > 0) {
        // console.log(
        //   'selectedCalendarIds already set, skipping initialization of all calendars.'
        // )
        return
      }

      // Default behavior: Select all calendars if no calendar is pre-selected
      let allCalendarIds = []
      this.calendarOptionsByOrganizationAndCategory.forEach(option => {
        option.categorizedCalendarCategories.forEach(category => {
          allCalendarIds = allCalendarIds.concat(
            category.calendarOptions.map(co => co.value)
          )
        })
        allCalendarIds = allCalendarIds.concat(
          option.uncategorizedCalendarOptions.map(co => co.value)
        )
      })

      // console.log(
      //   'Initializing selectedCalendarIds with all calendars:',
      //   allCalendarIds
      // )
      this.selectedCalendarIds = allCalendarIds
    },
    onCategoryChange(data) {
      let categoryCalendarIds = data.option.calendarOptions.map(co => co.value)
      let newSelectedCalendarIds = this.selectedCalendarIds.filter(
        id => !categoryCalendarIds.includes(id)
      )
      if (data.checked) {
        newSelectedCalendarIds = newSelectedCalendarIds.concat(
          categoryCalendarIds
        )
      }
      this.selectedCalendarIds = newSelectedCalendarIds
    },
    onCategorySelected(newSelectedCategories, organizationId) {
      let clonedSelectedCategoriesByOrganizationId = JSON.parse(
        JSON.stringify(this.selectedCategoriesByOrganizationId)
      )
      const previouslySelectedCategories =
        this.selectedCategoriesByOrganizationId[organizationId] || []
      const possibleCategories =
        this.categoriesByOrganizationId[organizationId] || []

      newSelectedCategories = previouslySelectedCategories
        .filter(c => possibleCategories.indexOf(c) === -1)
        .concat(newSelectedCategories)

      clonedSelectedCategoriesByOrganizationId[
        organizationId
      ] = newSelectedCategories
      this.selectedCategoriesByOrganizationId = clonedSelectedCategoriesByOrganizationId
    },
    onCalendarOptionsInput(newSelectedCalendarIds) {
      this.selectedCalendarIds = newSelectedCalendarIds
    },
  },
}
</script>

<style lang="scss">
@import '@asd20/ui/src/design/_variables.scss';

.asd20-event-filters {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-grow: 1;

  .asd20-viewport {
    margin-bottom: auto;
    flex-grow: 0;
  }
  .asd20-accordion__color {
    display: none;
  }

  .asd20-event-checkbox-list__item {
    .asd20-checkbox {
      width: auto;
      padding-left: space(0.25);
      padding-right: space(1);
      flex-grow: 1;
    }
  }
  .categorized-calendars {
    border-bottom: 1px solid var(--color__tertiary) !important;
    .asd20-checkbox-list__item {
      // padding-bottom: 0;
      .asd20-checkbox {
        width: auto;
        padding-left: space(1);
        padding-right: space(1);
        flex-grow: 1;
      }
    }
  }
  .nested-checkbox {
    background-color: #f4f5f6;
    .asd20-event-checkbox-list__item {
      .asd20-checkbox {
        width: auto;
        padding-left: space(0.25);
        padding-right: space(1);
        flex-grow: 1;
      }
    }
  }

  .asd20-event-checkbox-list {
    .asd20-checkbox-list__item,
    .asd20-event-checkbox-list__item {
      border-bottom: 1px solid var(--color__tertiary) !important;
    }
  }

  .school-calendar-button {
    justify-content: flex-start;
    flex-direction: row;
    width: 100%;
    text-align: left;
    padding: space(0.25) space(0.875);
    border-bottom: 1px solid #00000011;
    font-weight: bold;
    text-decoration: underline;
    // font-family: var(--website-typography__font-family-body);
    svg {
      margin-right: space(0.25);
    }
  }
}
</style>
